import React, { useCallback } from 'react'
import { Button } from '@demandlocal/components'
import { useModal } from '../../../components/useModal'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { toast } from 'react-hot-toast'
import { useDeleteSourceBySourceID } from '../../../api/dealership/customers/sources/useDeleteSourceBySourceID'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { queryClient } from '../../../api/queryClient'

type Props = {
  source: any
}

const DeleteCustomerSource = ({ source }: Props): JSX.Element => {
  const dealership = useDealership()
  const { closeModal } = useModal()
  const { mutateAsync: deleteSource, isLoading } = useDeleteSourceBySourceID()

  const onDeleteAudiencePress = useCallback(
    async (e) => {
      e.preventDefault()
      await deleteSource({ id: source.id })
      await queryClient.refetchQueries(['customerSources', dealership?.id])
      await queryClient.refetchQueries(['dealership', dealership?.id, 'stats'])
      toast.success('Source successfully deleted!')
      closeModal()
    },
    [deleteSource, source.id, closeModal, dealership?.id],
  )

  return (
    <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
      <div className={'flex flex-1 flex-col overflow-hidden'}>
        <div className={'font-bold text-blue-primary text-[28px]'}>
          Are you sure you want to delete this source?
        </div>
        <div className={'flex-1 flex items-center'}>
          <div className={'flex flex-1 flex-col gap-5'}>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Source name</span>
              <span className={'mt-3 font-medium text-base'}>
                {source.customer_source}
              </span>
            </div>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>
                Connection Type
              </span>
              <span className={'mt-3 font-medium text-base'}>{source.data_provider}</span>
            </div>
            <div className={'pb-5 border-b-2 flex-1 flex flex-col'}>
              <span className={'text-blue-primary font-bold text-sm'}>Customers</span>
              <span className={'mt-3 font-medium text-base'}>
                {formatNumber(source.customers_count)}
              </span>
            </div>
          </div>
        </div>
        <div className={'mb-10 text-primary-blue font-medium text-xs'}>
          <div
            className={
              'p-2 rounded-md w-1/2 font-bold bg-secondary-bittersweet-red/50 text-secondary-engineering-red'
            }>
            *If you delete this source, you will still be able to use the customers that
            are currently associated with it.
          </div>
        </div>
      </div>
      <div className={'h-20 items-center flex justify-between border-t-2 py-4 mt-4'}>
        <Button
          variant={'secondary'}
          onClick={(e) => {
            e.preventDefault()
            closeModal()
          }}>
          Close
        </Button>
        <Button disabled={isLoading} type={'submit'} onClick={onDeleteAudiencePress}>
          Delete source
        </Button>
      </div>
    </div>
  )
}

export default DeleteCustomerSource
